// src/pages/MyChatbots.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Menu,
    MenuItem,
    Alert,
    Snackbar,
    CircularProgress,
    ListItemIcon,
    ListItemText,
    useTheme,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import axiosInstance from '../../utils/axiosInstance';
import { useChatbots } from '../../contexts/ChatbotContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
    success: '#059669',
    warning: '#f59e0b',
    danger: '#dc2626',
};

export default function MyChatbots() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { chatbots, activeChatbot, switchActiveChatbot, refreshChatbots, isLoading } = useChatbots();
    const navigate = useNavigate();

    const [pendingChatbot, setPendingChatbot] = useState(null);
    const [loadingPending, setLoadingPending] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedBot, setSelectedBot] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [convertDialogOpen, setConvertDialogOpen] = useState(false);
    const [converting, setConverting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchPendingChatbot();
    }, []);

    const fetchPendingChatbot = async () => {
        try {
            setLoadingPending(true);
            const response = await axiosInstance.get('/chatbots/user/pending-process-bot');
            if (response.data.success && response.data.data.chatbot) {
                setPendingChatbot(response.data.data.chatbot);
            } else {
                setPendingChatbot(null);
            }
        } catch (error) {
            console.error('Error fetching pending chatbot:', error);
            setPendingChatbot(null);
        } finally {
            setLoadingPending(false);
        }
    };

    const handleSetDefault = async (chatbotId) => {
        try {
            await axiosInstance.put(`/chatbots/${chatbotId}/set-default`);
            setSnackbar({ open: true, message: 'Default chatbot updated!', severity: 'success' });
            refreshChatbots();
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to update default chatbot.', severity: 'error' });
            console.error(error);
        }
    };

    const handleMenuOpen = (event, bot) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedBot(bot);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedBot) return;
        setDeleting(true);
        try {
            await axiosInstance.delete(`/chatbots/${selectedBot.id}`);
            setSnackbar({ open: true, message: 'Chatbot deleted successfully!', severity: 'success' });
            setDeleteDialogOpen(false);
            setSelectedBot(null);
            refreshChatbots();
            fetchPendingChatbot();
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to delete chatbot.';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedBot(null);
    };

    const handleConvertClick = () => {
        handleMenuClose();
        setConvertDialogOpen(true);
    };

    const handleConvertConfirm = async () => {
        if (!selectedBot) return;
        const newType = selectedBot.chatbot_type === 'personal' ? 'business' : 'personal';
        setConverting(true);
        try {
            await axiosInstance.put(`/chatbots/${selectedBot.id}/convert-type`, { chatbot_type: newType });
            setSnackbar({ open: true, message: `Chatbot converted to ${newType} successfully!`, severity: 'success' });
            setConvertDialogOpen(false);
            setSelectedBot(null);
            refreshChatbots();
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to convert chatbot type.';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setConverting(false);
        }
    };

    const handleConvertCancel = () => {
        setConvertDialogOpen(false);
        setSelectedBot(null);
    };

    const handleCreateNewChatbot = () => navigate(`/chatbot/create`);
    const handleOpenChatbot = (bot) => switchActiveChatbot(bot.id, { navigateToDashboard: true });
    const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

    const getChatbotTypeChip = (type) => {
        const isPersonal = type === 'personal';
        return (
            <Chip
                icon={isPersonal ? <PersonIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' } }} /> : <BusinessIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' } }} />}
                label={isPersonal ? 'Personal' : 'Business'}
                size="small"
                sx={{
                    fontFamily,
                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                    height: { xs: '20px', sm: '24px' },
                    bgcolor: isPersonal ? 'rgba(139,92,246,0.1)' : 'rgba(59,130,246,0.1)',
                    color: isPersonal ? '#8b5cf6' : colors.accent,
                    border: 'none',
                    '& .MuiChip-icon': { color: 'inherit' },
                }}
            />
        );
    };

    if (isLoading) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                gap: 2,
            }}>
                <CircularProgress size={40} sx={{ color: colors.accent }} />
                <Typography sx={{ fontFamily, color: colors.textSecondary, fontSize: '0.9rem' }}>
                    Loading chatbots...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            fontFamily,
            maxWidth: '1200px',
            width: '100%',
            mx: 'auto',
        }}>
            {/* Header */}
            <Box sx={{ mb: { xs: 2, sm: 4 } }}>
                <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                    <Box sx={{
                        width: { xs: 44, sm: 56 },
                        height: { xs: 44, sm: 56 },
                        borderRadius: { xs: '12px', sm: '16px' },
                        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: { xs: 1.5, sm: 2 },
                        boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
                    }}>
                        <SmartToyIcon sx={{ color: '#fff', fontSize: { xs: 22, sm: 28 } }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{
                            fontFamily,
                            fontSize: { xs: '1.15rem', sm: '1.5rem' },
                            fontWeight: 700,
                            color: colors.textPrimary,
                            letterSpacing: '-0.02em',
                        }}>
                            My Chatbots
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            bgcolor: 'rgba(59,130,246,0.1)',
                            px: { xs: 1.5, sm: 2 },
                            py: 0.5,
                            borderRadius: '20px',
                        }}>
                            <Typography sx={{ fontFamily, fontSize: { xs: '0.75rem', sm: '0.85rem' }, fontWeight: 600, color: colors.accent }}>
                                {chatbots.length} {chatbots.length === 1 ? 'Bot' : 'Bots'}
                            </Typography>
                        </Box>
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: colors.textTertiary, mt: 1 }}>
                        Manage and configure your AI chatbots
                    </Typography>
                    <Box sx={{ mt: { xs: 1.5, sm: 2 }, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            startIcon={loadingPending ? <CircularProgress size={18} color="inherit" /> : (pendingChatbot ? <PlayArrowIcon /> : <AddCircleIcon />)}
                            onClick={handleCreateNewChatbot}
                            disabled={loadingPending}
                            fullWidth={isMobile}
                            sx={{
                                fontFamily,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                borderRadius: '12px',
                                px: { xs: 2, sm: 3 },
                                py: { xs: 1, sm: 1.25 },
                                background: pendingChatbot
                                    ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                                    : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                boxShadow: pendingChatbot
                                    ? '0 4px 16px rgba(245,158,11,0.3)'
                                    : '0 4px 16px rgba(59,130,246,0.3)',
                                '&:hover': {
                                    background: pendingChatbot
                                        ? 'linear-gradient(135deg, #d97706, #f59e0b)'
                                        : 'linear-gradient(135deg, #1d4ed8, #3b82f6)',
                                    transform: 'translateY(-1px)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            {loadingPending ? 'Loading...' : (pendingChatbot ? `Continue: ${pendingChatbot.chatbot_name}` : 'Create New Chatbot')}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Pending Setup Alert */}
            {pendingChatbot && (
                <Box sx={{
                    mb: { xs: 2, sm: 3 },
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: { xs: '10px', sm: '14px' },
                    bgcolor: 'rgba(245,158,11,0.08)',
                    border: '1px solid rgba(245,158,11,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1.5, sm: 2 },
                }}>
                    <Box sx={{
                        width: { xs: 34, sm: 40 },
                        height: { xs: 34, sm: 40 },
                        borderRadius: { xs: '8px', sm: '10px' },
                        bgcolor: 'rgba(245,158,11,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <WarningAmberIcon sx={{ color: colors.warning, fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: colors.textPrimary }}>
                            Setup Incomplete
                        </Typography>
                        <Typography sx={{
                            fontFamily,
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            color: colors.textSecondary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: { xs: 'nowrap', sm: 'normal' },
                        }}>
                            "{pendingChatbot.chatbot_name}" setup is incomplete. Current step: {pendingChatbot.current_process_step || 'Getting started'}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Chatbots Grid */}
            {chatbots.length === 0 ? (
                <Box sx={{
                    textAlign: 'center',
                    py: { xs: 4, sm: 8 },
                    px: { xs: 2, sm: 3 },
                    borderRadius: { xs: '12px', sm: '16px' },
                    bgcolor: 'rgba(255,255,255,0.7)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(0,0,0,0.06)',
                }}>
                    <Box sx={{
                        width: { xs: 56, sm: 80 },
                        height: { xs: 56, sm: 80 },
                        borderRadius: { xs: '14px', sm: '20px' },
                        bgcolor: 'rgba(59,130,246,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: { xs: 2, sm: 3 },
                    }}>
                        <RocketLaunchIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: colors.accent }} />
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600, color: colors.textPrimary, mb: 1 }}>
                        No Chatbots Yet
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: colors.textSecondary, mb: { xs: 2, sm: 3 }, maxWidth: 400, mx: 'auto' }}>
                        Create your first AI chatbot to engage customers, automate workflows, and scale your business.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddCircleIcon />}
                        onClick={handleCreateNewChatbot}
                        fullWidth={isMobile}
                        sx={{
                            fontFamily,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            borderRadius: '12px',
                            px: { xs: 2, sm: 4 },
                            py: { xs: 1, sm: 1.25 },
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            boxShadow: `0 4px 16px ${theme.palette.primary.main}30`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.2s ease',
                        }}
                    >
                        Create Your First Chatbot
                    </Button>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                    {chatbots.map((bot) => (
                        <Box
                            key={bot.id}
                            onClick={() => handleOpenChatbot(bot)}
                            sx={{
                                p: { xs: 1.5, sm: 2.5 },
                                borderRadius: { xs: '10px', sm: '14px' },
                                bgcolor: 'rgba(255,255,255,0.8)',
                                backdropFilter: 'blur(12px)',
                                border: bot.id === activeChatbot?.id
                                    ? `2px solid ${colors.accent}`
                                    : '1px solid rgba(0,0,0,0.06)',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'center' },
                                gap: { xs: 1.5, sm: 2.5 },
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                                    transform: { sm: 'translateY(-2px)' },
                                },
                            }}
                        >
                            {/* Top row on mobile: icon + name + more button */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 }, flex: 1, minWidth: 0 }}>
                                {/* Bot Icon */}
                                <Box sx={{
                                    width: { xs: 40, sm: 52 },
                                    height: { xs: 40, sm: 52 },
                                    borderRadius: { xs: '10px', sm: '12px' },
                                    bgcolor: bot.id === activeChatbot?.id ? 'rgba(59,130,246,0.15)' : 'rgba(0,0,0,0.04)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <SmartToyIcon sx={{
                                        fontSize: { xs: '1.3rem', sm: '1.75rem' },
                                        color: bot.id === activeChatbot?.id ? colors.accent : colors.textTertiary,
                                    }} />
                                </Box>

                                {/* Bot Info */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
                                        <Typography sx={{
                                            fontFamily,
                                            fontSize: { xs: '0.85rem', sm: '1rem' },
                                            fontWeight: 600,
                                            color: colors.textPrimary,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {bot.chatbot_name}
                                        </Typography>
                                        {getChatbotTypeChip(bot.chatbot_type)}
                                        {!bot.is_process_completed && (
                                            <Chip
                                                label="Setup Incomplete"
                                                size="small"
                                                sx={{
                                                    fontFamily,
                                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                    height: { xs: '18px', sm: '22px' },
                                                    bgcolor: 'rgba(245,158,11,0.1)',
                                                    color: colors.warning,
                                                }}
                                            />
                                        )}
                                        {bot.id === activeChatbot?.id && (
                                            <Chip
                                                label="Active"
                                                size="small"
                                                sx={{
                                                    fontFamily,
                                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                    height: { xs: '18px', sm: '20px' },
                                                    bgcolor: 'rgba(5,150,105,0.1)',
                                                    color: colors.success,
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography sx={{
                                        fontFamily,
                                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                        color: colors.textTertiary,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        ID: {bot.chatbot_identity?.slice(0, isMobile ? 8 : 12)}... • {new Date(bot.created_at).toLocaleDateString()}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Actions */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                                flexShrink: 0,
                            }} onClick={(e) => e.stopPropagation()}>
                                <Tooltip title={bot.is_default ? "Default chatbot" : "Make default"}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSetDefault(bot.id);
                                            }}
                                            disabled={bot.is_default}
                                            sx={{
                                                color: bot.id === activeChatbot?.id ? colors.accent : colors.textTertiary,
                                                '&:hover': { bgcolor: 'rgba(59,130,246,0.1)' },
                                            }}
                                        >
                                            {bot.is_default ? <StarIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} /> : <StarBorderIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Open">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenChatbot(bot);
                                        }}
                                        sx={{
                                            color: colors.textTertiary,
                                            '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', color: colors.accent },
                                        }}
                                    >
                                        <OpenInNewIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="More">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleMenuOpen(e, bot)}
                                        sx={{
                                            color: colors.textTertiary,
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                                        }}
                                    >
                                        <MoreVertIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        minWidth: { xs: 180, sm: 200 },
                        borderRadius: { xs: '10px', sm: '12px' },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        border: '1px solid rgba(0,0,0,0.06)',
                    }
                }}
            >
                {selectedBot && !selectedBot.is_process_completed && (
                    <MenuItem onClick={() => { handleMenuClose(); handleCreateNewChatbot(); }} sx={{ py: 1.25 }}>
                        <ListItemIcon><PlayArrowIcon fontSize="small" sx={{ color: colors.accent }} /></ListItemIcon>
                        <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.875rem' } }}>Continue Setup</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={handleConvertClick} sx={{ py: 1.25 }}>
                    <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.875rem' } }}>
                        Convert to {selectedBot?.chatbot_type === 'personal' ? 'Business' : 'Personal'}
                    </ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={handleDeleteClick}
                    disabled={selectedBot?.is_default}
                    sx={{ py: 1.25, color: selectedBot?.is_default ? colors.textTertiary : colors.danger }}
                >
                    <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: selectedBot?.is_default ? colors.textTertiary : colors.danger }} /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: '0.875rem' } }}>
                        {selectedBot?.is_default ? 'Cannot delete default' : 'Delete Chatbot'}
                    </ListItemText>
                </MenuItem>
            </Menu>

            {/* Delete Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: { xs: '12px', sm: '16px' }, mx: { xs: 2, sm: 'auto' } } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' }, px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
                    <DeleteIcon sx={{ color: colors.danger, fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />
                    Delete Chatbot
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
                    <DialogContentText sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                        Are you sure you want to delete <strong>"{selectedBot?.chatbot_name}"</strong>?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 1.5, sm: 2.5 }, pt: 0 }}>
                    <Button onClick={handleDeleteCancel} disabled={deleting} sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                        sx={{
                            fontFamily,
                            textTransform: 'none',
                            borderRadius: '10px',
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            bgcolor: colors.danger,
                            '&:hover': { bgcolor: '#b91c1c' },
                        }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Convert Dialog */}
            <Dialog
                open={convertDialogOpen}
                onClose={handleConvertCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: { xs: '12px', sm: '16px' }, mx: { xs: 2, sm: 'auto' } } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' }, px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
                    <SwapHorizIcon sx={{ color: colors.accent, fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />
                    Convert Chatbot Type
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
                    <DialogContentText component="div" sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        <Box sx={{ mb: 2 }}>
                            Convert <strong>"{selectedBot?.chatbot_name}"</strong> from{' '}
                            <Chip label={selectedBot?.chatbot_type} size="small" sx={{ mx: 0.5, fontFamily }} />
                            to{' '}
                            <Chip label={selectedBot?.chatbot_type === 'personal' ? 'business' : 'personal'} size="small" color="primary" sx={{ mx: 0.5, fontFamily }} />?
                        </Box>
                        <Alert severity="warning" sx={{ mb: 2, fontFamily, borderRadius: '10px', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            After converting, you will need to update the system prompt in Chatbot Settings.
                        </Alert>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 1.5, sm: 2.5 }, pt: 0 }}>
                    <Button onClick={handleConvertCancel} disabled={converting} sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConvertConfirm}
                        variant="contained"
                        disabled={converting}
                        startIcon={converting ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    >
                        {converting ? 'Converting...' : 'Yes, Convert'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%', fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
